import type { ReactNode } from 'react';
import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import {
  FileText,
  Link2,
  ListChecks,
  MessageCircle,
  Phone,
  Star,
} from 'lucide-react';
import { LogoMark } from './brand/Logo';

const ICONS = {
  documents: FileText,
  links: Link2,
  lists: ListChecks,
  starred: Star,
  chats: MessageCircle,
  calls: Phone,
} as const;

interface EmptyStateProps {
  kind: keyof typeof ICONS;
  title: string;
  body: string;
  action?: { label: string; icon?: ReactNode; onClick: () => void };
}

// Empty states (design: "Starred, empty & loading"): the app's icon on a
// card in a soft accent circle, the Twinden mark tucked at its corner, then
// one plain sentence and the one thing to do next.
export function EmptyState({ kind, title, body, action }: EmptyStateProps) {
  const Icon = ICONS[kind];
  return (
    <VStack
      spacing="14px"
      textAlign="center"
      px={{ base: 5, md: 10 }}
      py={{ base: 10, md: 16 }}
      maxW="420px"
      mx="auto"
    >
      <Box position="relative" w="168px" h="140px" aria-hidden="true">
        <Box
          position="absolute"
          left="20px"
          top="6px"
          w="128px"
          h="128px"
          borderRadius="full"
          bg="accent.tint"
        />
        <Box
          position="absolute"
          left="40px"
          top="28px"
          w="88px"
          h="88px"
          borderRadius="26px"
          bg="surface"
          boxShadow="0 10px 24px -10px rgba(31, 29, 27, 0.3)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="accent"
        >
          <Icon size={42} strokeWidth={1.75} />
        </Box>
        <LogoMark size={48} position="absolute" left="112px" top="84px" />
      </Box>
      <Heading as="h2" fontSize="24px" fontWeight={700} letterSpacing="-0.01em">
        {title}
      </Heading>
      <Text fontSize="16px" lineHeight="24px" color="ink.body">
        {body}
      </Text>
      {action && (
        <Button
          mt="6px"
          h="52px"
          px={6}
          fontSize="16px"
          leftIcon={action.icon as React.ReactElement | undefined}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </VStack>
  );
}
